import React, { useState } from "react";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import { useSelector } from "react-redux";

const Statement = () => {
  const [accountType, setAccountType] = useState("");
  const [name, setName] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isDateFrozen, setIsDateFrozen] = useState(false);
  const [errors, setErrors] = useState({});

  // Get user data from Redux store
  const { user } = useSelector((state) => state.auth);
  const userProfile = user?.data?.userProfile;

  // Helper function to format date to DD-MM-YYYY
  const formatToDDMMYYYY = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to parse DD-MM-YYYY to Date object
  const parseDDMMYYYY = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper function to convert DD-MM-YYYY to YYYY-MM-DD for date input
  const formatForDateInput = (dateString) => {
    if (!dateString) return "";
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  const handleAccountTypeChange = (e) => {
    const value = e.target.value;
    setAccountType(value);
    setErrors((prev) => ({ ...prev, accountType: "" }));
    if (value !== "LMBP") {
      setName("");
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setIsDateFrozen(true);

    // Clear any previous time range errors
    setErrors((prev) => ({ ...prev, timeRange: "" }));

    // Set dates based on selected period
    const today = new Date();
    const from = new Date();

    switch (period) {
      case "Monthly":
        from.setMonth(today.getMonth() - 1);
        break;
      case "Quarterly":
        from.setMonth(today.getMonth() - 3);
        break;
      case "Semi-Quarterly":
        from.setMonth(today.getMonth() - 6);
        break;
      case "Yearly":
        from.setFullYear(today.getFullYear() - 1);
        break;
      default:
        break;
    }

    // Format dates to DD-MM-YYYY
    setFromDate(formatToDDMMYYYY(from));
    setToDate(formatToDDMMYYYY(today));
  };

  const handleDateChange = (type, value) => {
    if (!isDateFrozen) {
      if (type === "from") {
        // Convert YYYY-MM-DD to DD-MM-YYYY
        const [year, month, day] = value.split("-");
        const formattedDate = `${day}-${month}-${year}`;
        setFromDate(formattedDate);
        // If user manually selects a date, clear the radio button selection
        setSelectedPeriod("");
      }
      if (type === "to") {
        // Convert YYYY-MM-DD to DD-MM-YYYY
        const [year, month, day] = value.split("-");
        const formattedDate = `${day}-${month}-${year}`;
        setToDate(formattedDate);
        // If user manually selects a date, clear the radio button selection
        setSelectedPeriod("");
      }
      // Clear any previous time range errors
      setErrors((prev) => ({ ...prev, timeRange: "" }));
    }
  };

  const handleDateFocus = () => {
    // If user focuses on date field, clear radio selection and unfreeze dates
    setSelectedPeriod("");
    setIsDateFrozen(false);
  };

  const validateForm = () => {
    const newErrors = {};

    // Account Type validation - only for OPERATION users
    if (userProfile === "OPERATION" && !accountType) {
      newErrors.accountType = "Account Type is required";
    }

    // Name validation (only if LMBP is selected) - only for OPERATION users
    if (userProfile === "OPERATION" && accountType === "LMBP" && !name) {
      newErrors.name = "Name is required";
    }

    // Time Range validation
    if (!selectedPeriod && (!fromDate || !toDate)) {
      newErrors.timeRange = "Please select either a period or date range";
    }

    // Date validation if manually selected
    if (!selectedPeriod && fromDate && toDate) {
      const from = parseDDMMYYYY(fromDate);
      const to = parseDDMMYYYY(toDate);
      if (from > to) {
        newErrors.timeRange = "From date cannot be after To date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = () => {
    if (validateForm()) {
      console.log({
        accountType,
        name,
        selectedPeriod,
        fromDate, // Already in DD-MM-YYYY format
        toDate, // Already in DD-MM-YYYY format
      });
      // Future API integration will go here
    }
  };

  const handleReset = () => {
    setAccountType("");
    setName("");
    setSelectedPeriod("");
    setFromDate("");
    setToDate("");
    setIsDateFrozen(false);
    setErrors({});
  };

  const handleViewReport = () => {
    console.log("View Report clicked");
    // Future functionality
  };

  const handleDownloadReport = () => {
    console.log("Download Report clicked");
    // Future functionality
  };

  return (
    <div>
      <HeaderWithBreadcrumb title="Statement" breadcrumb="Report" />

      <div className="w-[90%] relative bg-white shadow-md rounded-xl p-6 flex flex-col md:flex-row gap-6 pr-6">
        {/* Left Section */}
        <div className="w-full md:w-[50%] border rounded-xl p-5 relative">
          <div className="mb-6">
            {/* Dropdowns Section - Only for OPERATION users */}
            {userProfile === "OPERATION" && (
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                {/* Account Type Dropdown */}
                <div className="w-full md:w-1/2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={accountType}
                    onChange={handleAccountTypeChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff702b] ${
                      errors.accountType ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select Account Type</option>
                    <option value="LMBP">LMBP</option>
                    <option value="GL">GL</option>
                  </select>
                  {errors.accountType && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.accountType}
                    </p>
                  )}
                </div>

                {/* Name Dropdown - Conditionally Rendered */}
                {accountType === "LMBP" && (
                  <div className="w-full md:w-1/2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setErrors((prev) => ({ ...prev, name: "" }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff702b] ${
                        errors.name ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Name</option>
                      <option value="name1">Name 1</option>
                      <option value="name2">Name 2</option>
                    </select>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Time Range Section - Visible for ALL users */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Time Range <span className="text-red-500">*</span>
              </label>

              {errors.timeRange && (
                <p className="mb-3 text-sm text-red-600">{errors.timeRange}</p>
              )}

              {/* Radio Buttons for Period Selection */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Top Row */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="monthly"
                      name="period"
                      checked={selectedPeriod === "Monthly"}
                      onChange={() => handlePeriodChange("Monthly")}
                      className="h-4 w-4 text-[#ff702b] focus:ring-[#ff702b] border-gray-300"
                    />
                    <label
                      htmlFor="monthly"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Monthly
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="quarterly"
                      name="period"
                      checked={selectedPeriod === "Quarterly"}
                      onChange={() => handlePeriodChange("Quarterly")}
                      className="h-4 w-4 text-[#ff702b] focus:ring-[#ff702b] border-gray-300"
                    />
                    <label
                      htmlFor="quarterly"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Quarterly
                    </label>
                  </div>
                </div>

                {/* Bottom Row */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="semi-quarterly"
                      name="period"
                      checked={selectedPeriod === "Semi-Quarterly"}
                      onChange={() => handlePeriodChange("Semi-Quarterly")}
                      className="h-4 w-4 text-[#ff702b] focus:ring-[#ff702b] border-gray-300"
                    />
                    <label
                      htmlFor="semi-quarterly"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Semi - Quarterly
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="yearly"
                      name="period"
                      checked={selectedPeriod === "Yearly"}
                      onChange={() => handlePeriodChange("Yearly")}
                      className="h-4 w-4 text-[#ff702b] focus:ring-[#ff702b] border-gray-300"
                    />
                    <label
                      htmlFor="yearly"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Yearly
                    </label>
                  </div>
                </div>
              </div>

              {/* Date Range Inputs */}
              <div>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full md:w-1/2">
                    <label className="block text-xs text-gray-600 mb-1">
                      From Date
                    </label>
                    <input
                      type="date"
                      value={formatForDateInput(fromDate)}
                      onChange={(e) => handleDateChange("from", e.target.value)}
                      onFocus={handleDateFocus}
                      disabled={isDateFrozen}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff702b] ${
                        isDateFrozen
                          ? "bg-gray-100 cursor-not-allowed"
                          : "bg-white"
                      } ${
                        errors.timeRange && !selectedPeriod
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  <div className="w-full md:w-1/2">
                    <label className="block text-xs text-gray-600 mb-1">
                      To Date
                    </label>
                    <input
                      type="date"
                      value={formatForDateInput(toDate)}
                      onChange={(e) => handleDateChange("to", e.target.value)}
                      onFocus={handleDateFocus}
                      disabled={isDateFrozen}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff702b] ${
                        isDateFrozen
                          ? "bg-gray-100 cursor-not-allowed"
                          : "bg-white"
                      } ${
                        errors.timeRange && !selectedPeriod
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generate and Reset Buttons */}
          <div className="border-t border-gray-300 pt-4 mt-4">
            <div className="flex justify-center gap-3">
              <button
                onClick={handleGenerate}
                className="px-4 py-2 w-[120px] bg-[#ff702b] text-white rounded-lg hover:bg-[#e55a1a] focus:outline-none focus:ring-2 focus:ring-[#ff702b]"
              >
                Generate
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 w-[120px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="w-full md:w-[50%] bg-[#FFF5EB] border rounded-xl p-5">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Message:
            </h3>
            <p className="text-gray-600">This is Statement Report</p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleViewReport}
              className="w-full py-3 bg-[#ff702b] text-white rounded-lg hover:bg-[#e55a1a] focus:outline-none focus:ring-2 focus:ring-[#ff702b] transition duration-200"
            >
              View Report
            </button>
            <button
              onClick={handleDownloadReport}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200"
            >
              Download Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statement;
